/**
 * MCB Document Automation Agent — Vite entry point.
 *
 * This file bootstraps the React application when using the Vite dev server
 * or the production build. The actual components live in app.jsx (migrated
 * from static/app.js).
 */
import React from "react";
import { createRoot } from "react-dom/client";
import "./responsive.css";
import App from "./app.jsx";

// Service worker — production only.
//
// Registering in dev would cache the shell in front of Vite's HMR and
// produce stale-module confusion that looks like a code bug. It caches
// the app shell only and never /api/ responses; see public/sw.js for
// why that boundary matters in an app holding medical records.
if ("serviceWorker" in navigator && import.meta.env.PROD) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch((err) => {
      // Not fatal — the app works fine uninstalled. Worth a line in the
      // console rather than an unhandled rejection.
      console.warn("Service worker registration failed:", err);
    });
  });

  // Reload once when a new worker takes over.
  //
  // The worker calls skipWaiting/clients.claim, so it starts serving
  // the new build immediately — but the page already running still has
  // the old JS in memory. Without this, an update needs two launches:
  // one to swap the worker, another to actually load new code. In the
  // installed app that was indistinguishable from the update never
  // arriving.
  //
  // Whether a worker was already in charge when this page loaded, read
  // NOW rather than inside the handler.
  //
  // The previous version checked `navigator.serviceWorker.controller`
  // inside the controllerchange listener to detect a first install. By
  // that point the new worker has claimed, so the property is always
  // set and the check never skipped anything — it did the opposite of
  // what it claimed. Every first install reloaded the page, and landing
  // on /auth/callback when that fired interrupted the OAuth code
  // exchange: the browser looked like it was loading the app and then
  // sat there, signed out.
  const hadControllerAtLoad = !!navigator.serviceWorker.controller;

  let refreshing = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (refreshing) return;
    // First install: this page is already running the current build.
    // Reloading buys nothing and costs whatever is mid-flight.
    if (!hadControllerAtLoad) return;
    // Never reload during the OAuth handshake. The callback is a
    // one-shot exchange of a code that cannot be replayed, so a reload
    // here does not retry the login — it loses it.
    if (window.location.pathname.startsWith("/auth/")) return;

    refreshing = true;
    window.location.reload();
  });
}

const root = createRoot(document.getElementById("root"));
root.render(React.createElement(App));
